import { useCallback, useRef, useState } from 'react';
import { Mail } from 'lucide-react';
import { Button, IconHighlight, Input, ModalLayout } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { NO_HEADER_LINE, WIDE_MODAL } from '../shared/modalClasses';
import { useEmailTaken } from '../../hooks/useEmailTaken';
import { isValidEmail } from '../../utils/parseEmails';

/**
 * Edit the email of a pending member; saving sends the invitation again to the new address. Mount
 * it only while it is open (it starts from the current email each time). Save is disabled until
 * the address is valid, different from the current one and not used by another member; the dialog
 * closes when `onSave` resolves `true`.
 *
 * @param {object} props
 * @param {string} props.memberId The member being edited (their own email is not a duplicate).
 * @param {string} props.email Current email, the initial value.
 * @param {boolean} [props.resends=true] Saving sends the invitation again (it went out already). False for a
 *   member who was never invited: only the address is corrected.
 * @param {() => void} props.onClose
 * @param {(email: string) => Promise<boolean>} props.onSave Resolves `true` when saved and `false` when
 *   it failed; it never throws (the caller toasts its own failures). A throw is still survived: Save is released.
 */
function EditEmailModal({ memberId, email, resends = true, onClose, onSave }) {
  const { t } = useTranslation('people-access');
  const [value, setValue] = useState(email);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const trimmed = value.trim();
  const isValid = isValidEmail(trimmed);
  const isChanged = trimmed.toLowerCase() !== email.trim().toLowerCase();
  const isTaken = useEmailTaken(trimmed, memberId, isValid && isChanged);
  const canSave = isValid && isChanged && !isTaken;
  const error = trimmed && !isValid ? t('memberDetail.editEmail.invalid') : isTaken ? t('memberDetail.editEmail.taken') : '';

  const handleChange = useCallback((event) => setValue(event.target.value), []);

  const handleSave = useCallback(async () => {
    if (!canSave || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    let ok = false;
    try {
      ok = await onSave(trimmed);
    } catch {
      // The caller reports its own failures; the modal stays open to try again.
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
    if (ok) onClose();
  }, [canSave, onSave, trimmed, onClose]);

  const suffix = resends ? '' : 'NotSent';

  return (
    <ModalLayout
      isOpen
      onClose={onClose}
      useBodyPortal
      title={t('memberDetail.editEmail.title')}
      subtitle={t(`memberDetail.editEmail.description${suffix}`)}
      closeAriaLabel={t('memberDetail.confirm.close')}
      closeDisabled={submitting}
      titlePrefix={<IconHighlight icon={Mail} bgColor="Primary" iconColor="Primary" size="medium" />}
      panelClassName={NO_HEADER_LINE}
      maxWidthClassName={WIDE_MODAL}
    >
      <div className="px-6 py-4">
        <Input
          type="email"
          aria-label={t('memberDetail.editEmail.label')}
          placeholder={t('memberDetail.editEmail.placeholder')}
          value={value}
          onChange={handleChange}
          error={error || undefined}
          autoFocus
        />
      </div>
      <div className="flex justify-end gap-2 px-6 py-4">
        <Button variant="secondary" size="md" onClick={onClose} disabled={submitting}>
          {t('memberDetail.confirm.cancel')}
        </Button>
        <Button size="md" onClick={handleSave} disabled={!canSave} loading={submitting}>
          {t(`memberDetail.editEmail.save${suffix}`)}
        </Button>
      </div>
    </ModalLayout>
  );
}

export default EditEmailModal;
